import { createSelector } from 'reselect';
import { every, uniq } from 'lodash';
import { getAccounts, getPriorBusinessDay } from '../account/AccountSelectors';
import { applyPerformanceFilters } from './PerformancePageUtils';
import { minInceptionDate } from 'services/Calculation';
import {
  ACTIVE_PLUS,
  ADVICE_MANAGED_CLASS,
  BROKERAGE,
  COLLEGE_SAVINGS_CLASS,
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  MUTUAL_FUND,
  NON_RETIREMENT_CLASS,
  OWNED_CLASS,
  RETIREMENT_CLASS,
  VIEW_ONLY_CLASS
} from 'services/Products';
import {
  AK_ONLY_COLLEGE_SAVINGS_LABEL,
  ALL_LABEL,
  APP_LABEL,
  BROKERAGE_LABEL,
  COLLEGE_SAVINGS_LABEL,
  MUTUAL_FUND_LABEL,
  NON_RETIREMENT_LABEL,
  OWNED_LABEL,
  RAS_LABEL,
  RETIREMENT_LABEL,
  VIEW_ONLY_LABEL
} from 'services/Filters';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';
import { isRetirementAdvisoryServiceClient } from '../customer/CustomerSelectors';

export const getRateOfReturnFilter = (state) => state.rateOfReturn;
const getPerformancePage = (state) => state.performancePage;

const getFilteredSelection = createSelector(
  getRateOfReturnFilter,
  (performanceFilter) => {
    const { filter } = performanceFilter;

    switch (filter.label) {
      case RAS_LABEL: {
        return ADVICE_MANAGED_CLASS;
      }
      case APP_LABEL: {
        return ACTIVE_PLUS;
      }
      case MUTUAL_FUND_LABEL: {
        return MUTUAL_FUND;
      }
      case BROKERAGE_LABEL: {
        return BROKERAGE;
      }
      case COLLEGE_SAVINGS_LABEL: {
        return COLLEGE_SAVINGS_CLASS;
      }
      case AK_ONLY_COLLEGE_SAVINGS_LABEL: {
        return COLLEGE_SAVINGS_CLASS;
      }
      case RETIREMENT_LABEL: {
        return RETIREMENT_CLASS;
      }
      case NON_RETIREMENT_LABEL: {
        return NON_RETIREMENT_CLASS;
      }
      case OWNED_LABEL: {
        return OWNED_CLASS;
      }
      case VIEW_ONLY_LABEL: {
        return VIEW_ONLY_CLASS;
      }
      case ALL_LABEL:
      default:
        return 0;
    }
  }
);

export const getAccountsForRateOfReturn = createSelector(
  getPerformancePage,
  getRateOfReturnFilter,
  getAccounts,
  (performancePage, portfolioGrowthFilter, accounts) =>
    applyPerformanceFilters(performancePage, portfolioGrowthFilter, accounts)
);

/*
Deprecation - references can likely be updated to only use portfolio as of
 */
export const getAsOfDateForRateOfReturn = createSelector(
  getPortfolioAsOfDate,
  (portfolioAsOfDate) => portfolioAsOfDate
);

export const getInceptionDateForRateOfReturn = createSelector(
  getAccountsForRateOfReturn,
  getPriorBusinessDay,
  (accounts, priorBusinessDay) => minInceptionDate(accounts) || priorBusinessDay
);

export const getIsOnlyActivePlusSelectedForRateOfReturn = createSelector(
  getAccountsForRateOfReturn,
  (accounts) => every(accounts, (a) => isActivePlus(a.productType))
);

export const getShowMultipleActivePlusForRateOfReturn = createSelector(
  getAccountsForRateOfReturn,
  (accounts) =>
    accounts.filter((account) => isActivePlus(account.productType)).length > 1
);

export const getIsOnlyActiveAdvisorySelectedForRateOfReturn = createSelector(
  getAccountsForRateOfReturn,
  (accounts) => every(accounts, (a) => isActiveAdvisory(a.productType))
);

export const getShowMultipleActiveAdvisoryForRateOfReturn = createSelector(
  isRetirementAdvisoryServiceClient,
  getAccountsForRateOfReturn,
  (isRetirementAdvisoryServiceClient, accounts) => {
    // only display warning if the user is not a RAS client but can see multiple households (agent)
    const householdIds = uniq(
      accounts
        .filter((account) => isActiveAdvisory(account.productType) && account.financialPlanningGroupId != null)
        .map((account) => account.financialPlanningGroupId)
    );
    return householdIds.length > 1 && !isRetirementAdvisoryServiceClient;
  }
);

export const getShowAllPRRGraphWarningsForRateOfReturn = createSelector(
  getAccountsForRateOfReturn,
  getShowMultipleActivePlusForRateOfReturn,
  getShowMultipleActiveAdvisoryForRateOfReturn,
  (accounts, hasMultipleActivePlus, hasMultipleAdviceManaged) => {
    // shows if every account is brokerage, APP, and RAS + if there are multiple APP, RAS, and if there's one+ brokerage
    const hasBrokerage = accounts.some((account) =>
      isBrokerage(account.productType)
    );
    return (
      accounts.every(
        (account) =>
          isActivePlus(account.productType) ||
          isActiveAdvisory(account.productType) ||
          isBrokerage(account.productType)
      ) &&
      hasMultipleAdviceManaged &&
      hasMultipleActivePlus &&
      hasBrokerage
    );
  }
);

export const getShowActivePlusWarningForRateOfReturn = createSelector(
  getIsOnlyActivePlusSelectedForRateOfReturn,
  getShowMultipleActivePlusForRateOfReturn,
  getFilteredSelection,
  (onlyActivePlus, hasMultipleActivePlus, filter) =>
    (onlyActivePlus || isActivePlus(filter)) && hasMultipleActivePlus
);

export const getShowActiveAdvisoryWarningForRateOfReturn = createSelector(
  getIsOnlyActiveAdvisorySelectedForRateOfReturn,
  getShowMultipleActiveAdvisoryForRateOfReturn,
  getFilteredSelection,
  (onlyActiveAdvisory, hasMultipleActiveAdvisory, filter) =>
    (onlyActiveAdvisory || filter === ADVICE_MANAGED_CLASS) &&
    hasMultipleActiveAdvisory
);
